import { useEffect, useState } from 'react';
import { MODULES, fetchReviewState, summarizeReview, REVIEW_API_BASE } from '../lib/modules';

type Row = {
  id: string;
  title: string;
  category: string;
  lessonCount: number;
  reviewed: number;
  inProgress: number;
  comments: number;
  pct: number;
};

export function CurriculumReview() {
  const [rows, setRows] = useState<Row[] | null>(null);

  useEffect(() => {
    (async () => {
      const states = await Promise.all(MODULES.map((m) => fetchReviewState(m.id)));
      const out: Row[] = MODULES.map((m, i) => {
        const s = summarizeReview(states[i], m.lessonCount);
        return {
          id: m.id,
          title: m.title,
          category: m.category,
          lessonCount: m.lessonCount,
          reviewed: s.reviewedLessons,
          inProgress: s.inProgressLessons,
          comments: s.commentCount,
          pct: s.pct,
        };
      });
      setRows(out);
    })();
  }, []);

  const totalLessons = MODULES.reduce((n, m) => n + m.lessonCount, 0);
  const totalReviewed = rows?.reduce((n, r) => n + r.reviewed, 0) ?? 0;
  const totalComments = rows?.reduce((n, r) => n + r.comments, 0) ?? 0;
  const overallPct = rows ? Math.round((totalReviewed / totalLessons) * 100) : null;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Curriculum</div>
          <h1 className="page-title">Review Progress</h1>
          <p className="page-sub">Leah's review state across the 15 Hawaii modules. Pulled live from{' '}
            <code>{REVIEW_API_BASE}/api/comments</code>.</p>
        </div>
        <a className="btn" href="https://review.rootegra.com" target="_blank" rel="noreferrer">
          Open review tool ↗
        </a>
      </div>

      <div className="kpi-grid">
        <Kpi label="Reviewed lessons" value={rows ? `${totalReviewed} / ${totalLessons}` : null} tone="moss" />
        <Kpi label="Overall % complete" value={overallPct == null ? null : `${overallPct}%`} tone="clay" />
        <Kpi label="Total comments" value={totalComments} tone="dusty" />
        <Kpi label="Modules" value={MODULES.length} tone="sage" />
      </div>

      <section className="panel" style={{ padding: 0 }}>
        <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Module</th>
              <th>Category</th>
              <th className="num">Lessons</th>
              <th className="num">Reviewed</th>
              <th className="num">In&nbsp;progress</th>
              <th className="num">Comments</th>
              <th style={{ width: 220 }}>Progress</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows == null && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center' }} className="muted">Loading…</td></tr>
            )}
            {rows?.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.title}</strong></td>
                <td><span className={`chip ${categoryChip(r.category)}`}>{r.category}</span></td>
                <td className="num">{r.lessonCount}</td>
                <td className="num">{r.reviewed}</td>
                <td className="num">{r.inProgress}</td>
                <td className="num">{r.comments}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="bar" style={{ flex: 1 }}><i style={{ width: `${r.pct}%` }} /></div>
                    <span className="mod-pct">{r.pct}%</span>
                  </div>
                </td>
                <td>
                  <a className="btn btn--ghost"
                     href={`https://review.rootegra.com/?module=${encodeURIComponent(r.id)}`}
                     target="_blank" rel="noreferrer">
                    open ↗
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </section>
    </>
  );
}

function categoryChip(c: string) {
  if (c === 'foundational') return 'chip--info';
  if (c === 'chemical') return 'chip--warn';
  if (c === 'practical') return 'chip--ok';
  return '';
}

function Kpi({ label, value, tone }: { label: string; value: number | string | null; tone: 'moss' | 'clay' | 'dusty' | 'sage' }) {
  return (
    <div className={`kpi kpi--${tone}`}>
      <div className="kpi-top">
        <div className="kpi-label">{label}</div>
      </div>
      <div className="kpi-num">{value == null ? '—' : value}</div>
    </div>
  );
}
